import AbsenceStats from '@models/AbsenceStats';
import CourseDetailsStat from '@models/CourseDetailsStat';
import axios, { AxiosRequestConfig, Method } from 'axios';
import { useStore } from '@store/appStore';
import moment, { Moment } from 'moment';
import ClientSettings from '@models/ClientSettings';
import CourseInfo from '@models/CourseInfo';
import SearchResult from '@models/SearchResult';
import TotalPercentageStat from '@models/TotalPercentageStat';
import OverTimeCoursePercentage from '@models/OverTimeCoursePercentage';
import CoursePercentage from '@models/CoursePercentage';
import PerCourseAbsenceType from '@models/PerCourseAbsenceType';
import StudentsAbsence from '@models/StudentsAbsence';
import ScheduleItem from '@models/ScheduleItem';
import Student from '@models/Student';
import UnmarkedScheduleItem from '@models/UnmarkedScheduleItem';
import ENDPOINTS from '@models/Endpoint';
import CoursePercentageOverTime from '@models/CoursePercentageOverTime';
import RegistrationStats from '@models/RegistrationStats';
import CoursePercentageStats from '@models/CoursePercentageStats';
import StudentNote from '@models/StudentNote';
import NoteTags from '@models/NoteTags';
import { DIRECT_ROUTE_BUILDERS } from './directRoutes';
import Bugsnag from '@utils/bugsnag';
import { getFreshToken, tokenIsExpired } from '@utils/tokenRefresh';

// Next.js's env variable system is kinda weird
// it's using DefinePlugin and it prevents it from
// being destructured, so you have to use the full
// declaration e.g process.env.ENV
const instance = axios.create();

// The profile view fires getUserInfo for the same username from several components at once;
// concurrent calls share one request (removed as soon as it settles, so nothing goes stale).
const inFlightUserInfo = new Map<string, Promise<any>>();

// Endpoints that bypass the UnmanagedApi middle layer and call ClientDataAPI_WS's webservice
// directly. UnmanagedApi doesn't forward the Authorization header, so anything relying on the
// server-side role check (currently only the 3 Regi_Mark* pilot endpoints - see
// RoleAuthorizationFilter) has to skip it regardless. The rest of this list is a routing-only
// migration (still just AuthenticationAppKeyFilter server-side, no role check yet) - ported from
// UnmanagedApi's per-function proxy logic via directRoutes.ts. A handful of endpoints
// (search, Export_CourseStatistic, Export_Student, GetStreak4Student,
// Student_Stat_CourseChartOverTime) have more complex proxy logic that wasn't auto-portable and
// are intentionally left on the middle layer for now - see directRoutes.ts's generator notes.
// GETMISSINGREGISTRATIONS(BYTEACHER)/GETSICKEVENT4ALLSTUDENTS/ADM_COURSESTAT2EXCEL/
// ADM_SUBJECTONCOURSESTAT2EXCEL were hand-verified against ClientDataAPI_WS's CourseController.cs
// routes; all four now also have a proxy function in UnmanagedApi (used in demo). Two orphaned
// endpoints, UPDATETEACHERNAME and GET_TEAMSBOT_MEETING_PRESENCE, are deliberately NOT included
// here: UpdateTeacherName has no matching route in ClientDataAPI_WS at all, and
// Get_TeamsBot_Meeting_Presence's endpoint constant doesn't match its actual registered route
// name (missing vs. present underscore) - both appear to have been silently 404ing already,
// pre-existing bugs unrelated to this migration. Left on the middle layer unchanged rather than
// guessed at.
// UPDATESTUDENTROSTER is now gated by RoleAuthorizationFilter server-side
// (SuperAdminGroupname) - the auto-generated proxy's path-segment branch in
// UnmanagedApi/src/functions/UpdateStudentRoster.ts is dead code (the frontend always sends
// sis_id/course_sis_id/username/dates in the body with no query params, so that proxy always
// took its bare-URL branch anyway), so the generic fallback in constructDirectWebserviceURL
// already builds the right URL with no new case needed - added here instead of leaving it on
// the middle layer, which would otherwise 401 every call once the role check goes live.
const DIRECT_WEBSERVICE_ENDPOINTS: ENDPOINTS[] = [
  ENDPOINTS.REGI_MARKSTUDENTSTATUSBYTIMEBLOCKIDANDSTUDENTID,
  ENDPOINTS.REGI_MARKALLSTUDENTSSTATUSBYTIMEBLOCKID,
  ENDPOINTS.REGI_MARKTIMEBLOCKID,
  ENDPOINTS.GETMISSINGREGISTRATIONSBYTEACHER,
  ENDPOINTS.GETMISSINGREGISTRATIONS,
  ENDPOINTS.GETSICKEVENT4ALLSTUDENTS,
  ENDPOINTS.ADM_COURSESTAT2EXCEL,
  ENDPOINTS.ADM_SUBJECTONCOURSESTAT2EXCEL,
  ENDPOINTS.UPDATESTUDENTROSTER,
  ...(Object.keys(DIRECT_ROUTE_BUILDERS) as ENDPOINTS[]),
];

// false: everything goes through UnmanagedApi again, which now passes the user's token on to the
// webservice (its role check still works). true: the endpoints above call the webservice directly.
const USE_DIRECT_WEBSERVICE = false;

// Demo has no signed-in user token for the backend's role check, so it goes back through
// UnmanagedApi (which injects the ApiKey server-side) instead of calling the webservice directly.
function isDemoMode(): boolean {
  return process.env.NEXT_PUBLIC_IS_DEMO === 'true' || sessionStorage.getItem('demo') === '1';
}

// UnmanagedApi injects this server-side today, invisible to the browser. Calling the webservice
// directly means the browser has to send it instead - a known, accepted exposure for now (the
// same shared key already gets a fresh worthwhile check from RoleAuthorizationFilter on these
// endpoints; AuthenticationAppKeyFilter alone was never meant to be the real security boundary).
const DIRECT_WEBSERVICE_API_KEY = '38AC2841-E2F8-4468-9ED6-9D60E3641EDBX';

// Attaches the signed-in user's token so the backend can independently verify the caller's
// role (it derives username/school itself from the token via GraphIdentityService, rather
// than trusting a client-supplied id), instead of trusting client-side page gating alone.
// Mobile never populates `token` - its Graph token comes from the native MsAuthPlugin login and
// lives on user.access_token instead (see the `if (!mobile)` guard in useAuthBase.getClientData).
instance.interceptors.request.use(async (config) => {
  const { user } = useStore.getState();
  const bearer = (await getFreshToken()) || user?.access_token;
  if (bearer) {
    config.headers.set('Authorization', `Bearer ${bearer}`);
  }
  return config;
});

export default {
  async Log(log: any): Promise<any> {
    return axios
      .post(
        '/api/Log/',
        JSON.stringify(log)
      )
      .catch((error) => {
        console.log(error);
      });
  },
  async Delete_Reward(id: string) {
    return callApi({
      API_URL: ENDPOINTS.DELETEREWARD,
      params: { id },
    });
  },
  async getUserDetails(username: string, userid: string) {
    return callApi({
      API_URL: ENDPOINTS.GETUSERDETAILS,
      body: { username, userid },
      method: 'POST',
      params: {},
    });
  },
  async GetRedeemRewards_Log(timezone: string, language: string) {
    return callApi({
      API_URL: ENDPOINTS.GETREDEEMREWARDSLOG,
      params: {},
      body: { timezone, language },
      method: 'POST',
    });
  },
  async IsRewardRedeemed(qrcode: string) {
    return callApi({
      API_URL: ENDPOINTS.ISREWARDREDEEMED,
      params: {},
      body: { qrcode },
      method: 'POST',
    });
  },
  async Reward_UnusedDeleteReservation(qrcode: string) {
    return callApi({
      API_URL: ENDPOINTS.REWARDUNUSEDDELETERESERVATION,
      params: {},
      body: { qrcode },
      method: 'POST',
    });
  },
  async Student_Stat_GetRewards(username: string, language: string) {
    return callApi({
      API_URL: ENDPOINTS.STUDENT_STAT_GETREWARDS,
      params: { username, language },
    });
  },
  async MakeReward_Reservation(reward_id: number, username: string, duration: number) {
    return callApi({
      API_URL: ENDPOINTS.MAKEREWARDRESERVATION,
      params: {},
      body: { reward_id, username, duration },
      method: 'POST',
    });
  },
  async SetLUDUS_CaseworkerEmailReport(json: string) {
    return callApi({
      API_URL: ENDPOINTS.SETLUDUSCASEWORKEREMAILREPORT,
      params: {},
      body: json,
      method: 'POST',
    });
  },
  async GetLUDUS_CaseworkerReportsRecepients(login: string) {
    return callApi({
      API_URL: ENDPOINTS.GETLUDUSCASEWORKERREPORTSRECEPIENTS,
      params: { login },
    });
  },

  async GetCaseWorker_Students(json: string, username: string, note_type: Number, fromDate: string, toDate: string, useMerit: boolean, usePause: boolean) {

    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate, toDate };
    }

    return callApi({
      API_URL: ENDPOINTS.GETCASEWORKERSTUDENTS,
      params: { username, note_type, useMerit, usePause },
      body: json,
      filterDates: filterDates,
      method: 'POST',
    });
  },
  async UpdateTeacherName(firstname: string, lastname: string, name: string, username: string) {
    return callApi({
      API_URL: ENDPOINTS.UPDATETEACHERNAME,
      params: { firstname, lastname, name, username },
    });
  },
  async MergeLogin(gui: string, username: string, upn: string, firstname: string, lastname: string, displayname: string, user_groups: string, email?: string) {
    return callApi({
      API_URL: ENDPOINTS.MERGELOGIN,
      params: {},
      method: 'POST',
      body: { gui, username, upn, firstname, lastname, displayname, user_groups, email },
    });
  },
  async Redeem_Reward(clerk_username: string, qrcode: string) {
    return callApi({
      API_URL: ENDPOINTS.REDEEMREWARD,
      params: {},
      body: { clerk_username, qrcode },
      method: 'POST',
    });
  },
  async GetRewards(language: string) {
    return callApi({
      API_URL: ENDPOINTS.GETREWARDS,
      params: { language },
    });
  },
  async Modify_Reward(
    id: number,
    title: string,
    description: string,
    fromdate: string,
    todate: string,
    icon: number,
    cost: number,
    limit: number,
    active: boolean,
    stock: number,
    language: string,
    user_login: string,
  ) {
    return callApi({
      API_URL: ENDPOINTS.MODIFYREWARD,
      params: {},
      body: { id, title, description, fromdate, todate, icon, cost, limit, active, stock, language, user_login },
      method: 'POST',
    });
  },
  async GetStudentRosterLog() {
    return callApi({
      API_URL: ENDPOINTS.GETSTUDENTROSTERLOG,
      params: {},
    });
  },
  async UpdateStudentRoster(sis_id: string, course_sis_id: string, username: string, dates: any) {
    return callApi({
      API_URL: ENDPOINTS.UPDATESTUDENTROSTER,
      params: {},
      body: { sis_id, course_sis_id, username, startdate: dates.startdate, enddate: dates.enddate },
      method: 'POST',
    });
  },
  async GetStudentRoster(username: string) {
    return callApi({
      API_URL: ENDPOINTS.GETSTUDENTROSTER,
      params: { username },
    });
  },
  async GetInitialsRegistrations_Types() {
    return callApi({
      API_URL: ENDPOINTS.GETINITIALREGISTRATIONS_TYPES,
      params: {},
    });
  },
  async getSickEvent4Student(userId: string, daysback: number) {
    return callApi({
      API_URL: ENDPOINTS.GETSICKEVENT4STUDENT,
      params: { userId, daysback },
    });
  },
  async GetSickEvent4AllStudents(daysback: number) {
    return callApi({
      API_URL: ENDPOINTS.GETSICKEVENT4ALLSTUDENTS,
      params: { daysback },
    });
  },
  async deleteSickEvent4Student(absence_id: number) {
    return callApi({
      API_URL: ENDPOINTS.DELETESICKEVENT4STUDENT,
      params: { absence_id },
    });
  },
  async deleteStudentNote(note_id: number) {
    return callApi({
      API_URL: ENDPOINTS.DELETESTUDENTNOTE,
      params: { note_id },
    });
  },
  async getEventInfo(listOfReservationIds: string, studentId: string) {
    return callApi({
      API_URL: ENDPOINTS.GETEVENTINFO,
      params: { listOfReservationIds, studentId },
    });
  },
  async WondeGetAllTags() {
    return callApi({
      API_URL: ENDPOINTS.WONDEGETALLTAGS,
      body: {},
      params: {},
    });
  },
  async CreateEventInQueue(object_id, object_type, event_id, target_id?) {
    return callApi({
      API_URL: ENDPOINTS.CREATEEVENTINQUEUE,
      body: {},
      params: { object_id, object_type, event_id, target_id },
    });
  },

  async createLeaveEvent(
    student_id: string,
    student_username: string,
    author_id: string,
    author_username: string,
    author_displayname: string,
    json: any,
    notetext: string,
    tag_ids: string,
    absence_type: number,
    registration_method: number,
  ) {
    return callApi({
      API_URL: ENDPOINTS.CREATELEAVEEVENT,
      params: { student_id, student_username, author_id, author_username, tag_ids, absence_type, registration_method },
      body: { notetext: notetext, json: json, author_displayname: author_displayname },
      method: 'POST',
    });
  },
  async get_token(clientId: string, token: string) {
    return callApi({
      API_URL: ENDPOINTS.GET_TOKEN,
      params: { clientId, token },
    });
  },
  async checkifkohortexists() {
    return callApi({
      API_URL: ENDPOINTS.CHECKIFKOHORTEXISTS,
      params: {},
    });
  },
  async ListKohorts(fromDate: string, toDate: string) {
    let filterDates = { fromDate, toDate };
    return callApi({
      API_URL: ENDPOINTS.LISTKOHORTS,
      filterDates: filterDates,
    });
  },
  async KohortGetPercentages(kohortjson: string) {
    return callApi({
      API_URL: ENDPOINTS.KOHORTGETPERCENTAGES,
      params: {},
      method: 'POST',
      body: kohortjson,
    });
  },
  async GetToken(code: string, type: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GETACCESSTOKEN,
      params: { code, type },
    });
  },
  async getUserClassFavorites(username: string) {
    return callApi({
      API_URL: ENDPOINTS.GETUSERCLASSFAVORITES,
      params: { username },
    });
  },
  async getUserStudentFavorites(username: string, teacher_name: string, note_type: Number, fromDate: string, toDate: string, useMerit: boolean, usePause: boolean) {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate, toDate };
    }
    return callApi({
      API_URL: ENDPOINTS.GETUSERSTUDENTFAVORITES,
      params: { username, teacher_name, note_type, useMerit, usePause },
      filterDates: filterDates,
    });
  },
  async getUserTeacherFavorites(username: string) {
    return callApi({
      API_URL: ENDPOINTS.GETUSERTEACHERFAVORITES,
      params: { username },
    });
  },
  async setUserFavorites(username: string, objectId: string, type: Number) {
    return callApi({
      API_URL: ENDPOINTS.SETUSERFAVORITES,
      params: { username, objectId, type },
    });
  },
  async guardian_getstudents(guid?: string) {
    let params = {};
    if (guid) params = { guid }
    return callApi({
      API_URL: ENDPOINTS.GUARDIANGETSTUDENTS,
      params: params,
    });
  },
  async GetGuardians(groupname?: string) {
    return callApi({
      API_URL: ENDPOINTS.GETGUARDIANS,
      params: {},
      method: 'POST',
      body: groupname,
    });
  },
  async SetGuardian4Student(student_id: string, guardian_gui: string) {
    return callApi({
      API_URL: ENDPOINTS.SETGUARDIAN4STUDENT,
      params: {},
      method: 'POST',
      body: { student_id, guardian_gui },
    });
  },
  async DeleteGuardian4Student(student_id: string, guardian_gui: string) {
    return callApi({
      API_URL: ENDPOINTS.DELETEGUARDIAN4STUDENT,
      params: {},
      method: 'POST',
      body: { student_id, guardian_gui },
    });
  },
  async getClientData(email: string, login_domain: string = null) {
    return callApi({
      API_URL: ENDPOINTS.GET_CLIENTDATA,
      params: { email, login_domain },
    });
  },
  async getAllClientSetting(clientId: string) {
    return callApi({
      API_URL: ENDPOINTS.GET_ALLCLIENTSETTING,
      params: { clientId },
    });
  },
  async getAllClientSettings(tenantId: string) {
    return callApi({
      API_URL: ENDPOINTS.GET_ALLCLIENTSETTINGS,
      params: { tenantId },
    });
  },
  async getUserSetting(username: string) {
    return callApi({
      API_URL: ENDPOINTS.GET_USERSETTING,
      params: { username },
    });
  },
  async setUserSetting(username: string, settingId: Number, value: string, read_only: boolean) {
    return callApi({
      API_URL: ENDPOINTS.SET_USERSETTING,
      params: { username, settingId, value, read_only },
    });
  },
  async getClientTutorialOptions(email: string) {
    return callApi({
      API_URL: ENDPOINTS.GET_CLIENTTUTORIAL,
      params: { email },
    });
  },
  async setClientTutorial(email: string) {
    return callApi({
      API_URL: ENDPOINTS.SET_CLIENTTUTORIAL,
      params: { email },
    });
  },
  async setTeamsBotLanguage(username: string, language: string) {
    return callApi({
      API_URL: ENDPOINTS.SET_TEAMSBOT_LANGUAGE,
      params: { username, language },
    });
  },
  async getTeamsBotMeetingPresence(meetingId: string) {
    return callApi({
      API_URL: ENDPOINTS.GET_TEAMSBOT_MEETING_PRESENCE,
      params: { meetingId },
    });
  },
  async getTeamsBotPresence(meetingId: string, timeblockId: string, teacherUsername: string, fromDate?: string, toDate?: string) {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate, toDate };
    }
    return callApi({
      API_URL: ENDPOINTS.GET_TEAMSBOT_PRESENCE,
      params: { meetingId, timeblockId, teacherUsername },
      filterDates: filterDates,
    });
  },
  async ADM_CourseStat2Excel(course_sis_id: string, fromDate: string, toDate: string, useMerit: boolean, usePause: boolean): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.ADM_COURSESTAT2EXCEL,
      params: { course_sis_id, useMerit, usePause, fromDate, toDate },
    });
  },
  async ADM_SubjectOnCourseStat2Excel(course_sis_id: string, fromDate: string, toDate: string, courseDescription: string, useMerit: boolean, usePause: boolean): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.ADM_SUBJECTONCOURSESTAT2EXCEL,
      method: 'POST',
      body: { course_sis_id, fromDate, toDate, courseDescription, useMerit, usePause },
    });
  },
  async RedeemAuthorizationCode(code, verifier, redirect_uri): Promise<any> {
    let headers = { 'Content-Type': 'application/x-www-form-urlencoded' };

    const params = new URLSearchParams()

    params.append("grant_type", "authorization_code")
    params.append("redirect_uri", redirect_uri + "/login")
    params.append("code", code)
    params.append("code_verifier", verifier)
    params.append("client_id", "sandbox-fancy-tooth-102")
    params.append("client_secret", "ZSZG1Sc2ijTwb0fPGIahGzjltMH7pvm2pkaUgsN2jqO3Y99F")

    return axios
      .post(
        "https://aroscloud.sandbox.signicat.com/auth/open/connect/token",
        params,
        {
          headers: headers,
        },
      )
      .catch((error) => {
        console.log(error);
      });
  },

  async GetUserinfoMitId(accesstoken): Promise<any> {
    let headers = { 'Authorization': 'Bearer ' + accesstoken };

    return axios
      .get(
        "https://aroscloud.sandbox.signicat.com/auth/open/connect/userinfo",
        {
          headers: headers,
        },
      )
      .catch((error) => {
        console.log(error);
      });
  },
  async CheckMitidLogin(id): Promise<any> {
    return callApi({ API_URL: ENDPOINTS.CHECKMITIDLOGIN, params: { id } });
  },
  async getDniCaseWorkerClients(henviserKode: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GETDNICASEWORKERCLIENTS,
      params: { henviserKode },
    });
  },
  async SendMeetingInfo(meetingInfo: string, meetingId): Promise<any> {
    return axios
      .get('https://attenderteamschatbot.azurewebsites.net/meeting?meetinginfo=' + meetingInfo + '&meetingid=' + meetingId)
      .catch((error) => { });
  },
  async Regi_GetStudentNote(userName: string): Promise<StudentNote[]> {
    if (!userName) return Promise.reject('No userName provided');
    return callApi({
      API_URL: ENDPOINTS.REGI_GETSTUDENTNOTE,
      params: { userName },
    });
  },
  async Regi_GetRegistrationLog(sis_id: string, timeblock_id: string, timezone: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.REGI_GETREGISTRATIONLOG,
      params: { sis_id, timeblock_id, timezone },
    });
  },
  async GetNoteCounts4Student(Student_sis_id: string, reader_username: string, note_type: number): Promise<any[]> {
    return callApi({
      API_URL: ENDPOINTS.GETNOTECOUNTS4STUDENT,
      params: { Student_sis_id, reader_username, note_type },
    });
  },
  async Regi_SetStudentNote(
    student_sis_id: string,
    author_sis_id: string,
    author_UserName: string,
    author_DisplayName: string,
    note_type: string,
    tag_ids: string,
    text: string,
  ) {
    return callApi({
      API_URL: ENDPOINTS.REGI_SETSTUDENTNOTE,
      params: {
        student_sis_id,
        author_sis_id,
        author_UserName: encodeURI(author_UserName),
        author_DisplayName: encodeURI(author_DisplayName),
        note_type,
        tag_ids,
      },
      body: text,
      method: 'POST',
    });
  },
  async getDepartmentSettings(department: string) {
    return callApi({
      API_URL: ENDPOINTS.GETDEPARTMENTSETTINGS,
      method: 'POST',
      body: { department },
      params: {},
    });
  },
  async Regi_GetCoursesByTeacherName(userName: string, date: Moment, date2: Moment | null = null): Promise<ScheduleItem[]> {
    const formattedDate = date.format('YYYY-MM-DD');
    const formattedDate2 = date2 ? date2.format('YYYY-MM-DD') : date.format('YYYY-MM-DD');
    if (!userName) return Promise.reject('No userName provided');
    return callApi({
      API_URL: ENDPOINTS.REGI_GETCOURSESBYTEACHERNAME,
      params: { userName, formattedDate, formattedDate2 },
    });
  },
  async Regi_GetStudents4AllDayView(
    queryID: string,
    date: Moment,
    type: string,
    teacher_username: string,
    note_type_id: number,
  ): Promise<Student[]> {
    const formattedDate = date.format('YYYY-MM-DD');
    if (!queryID) return Promise.reject('No query ID provided');
    return callApi({
      API_URL: ENDPOINTS.POSTREQUEST,
      method: "POST",
      params: { requestmethod: "Regi_GetStudents4AllDayView" },
      body: { queryID, date: formattedDate, type, teacher_username, note_type_id },
    });
  },
  async Regi_GetCoursesByStudentName(userName: string, date: Moment, useMerit: boolean, usePause: boolean): Promise<ScheduleItem[]> {
    const formattedDate = date.format('YYYY-MM-DD');
    if (!userName) return Promise.reject('No userName provided');
    return callApi({
      API_URL: ENDPOINTS.REGI_GETCOURSESBYSTUDENTNAME,
      params: { userName, formattedDate, useMerit, usePause },
    });
    // return null;
  },
  async Regi_GetCoursesByCourseName(courseName: string, date: Moment): Promise<ScheduleItem[]> {
    const formattedDate = date.format('YYYY-MM-DD');
    if (!courseName) return Promise.reject('No courseName provided');
    return callApi({
      method: "POST",
      API_URL: ENDPOINTS.REGI_GETCOURSESBYCOURSENAME,
      params: { date: formattedDate },
      body: JSON.stringify(courseName)
    });
    // return null;
  },
  async Stats_GetAbsenceByStudentIDAndOrTimeBlockID(
    studentId: string,
    timeblockId: string,
    fromDate?: Moment,
    toDate?: Moment,
  ): Promise<AbsenceStats[]> {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate: moment(fromDate).format('YYYY-MM-DD'), toDate: moment(toDate).format('YYYY-MM-DD') };
    }
    return callApi({
      API_URL: ENDPOINTS.STATS_GETABSENCEBYSTUDENTIDANDORTIMEBLOCKID,
      params: { studentId, timeblockId },
      filterDates: filterDates,
    });
  },
  async Teacher_Stat_CoursePercentageOverTime(
    course_id: string,
    course_name: string,
    fromDate: Moment,
    toDate: Moment,
    useMerit: boolean,
    usePause: boolean
  ): Promise<OverTimeCoursePercentage[]> {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate: moment(fromDate).format('YYYY-MM-DD'), toDate: moment(toDate).format('YYYY-MM-DD') };
    }

    return callApi({
      method: 'POST',
      API_URL: ENDPOINTS.TEACHER_STAT_COURSEPERCENTAGEOVERTIME,
      body: { course_id, course_name },
      params: { useMerit, usePause },
      filterDates: filterDates,
    });
  },
  async search(
    query: string,
    admin: boolean = false,
    username: string,
    customSearchSP?: string,
    superAdmin: boolean = false,
    days?: number,
  ): Promise<SearchResult[]> {
    return callApi({
      API_URL: ENDPOINTS.SEARCH,
      params: { admin, superAdmin, days },
      body: { parameters: query, username: username, CustomSearchSP: customSearchSP },
      method: 'POST',
    });
  },
  async WrittenSearch(
    query: string,
    username: string
  ): Promise<SearchResult[]> {
    return callApi({
      API_URL: ENDPOINTS.WRITTENSEARCH,
      params: {},
      body: { parameters: query, username: username },
      method: 'POST',
    });
  },
  async SetManuelAssignmentAbsence(
    assignment_gui: string,
    student_id: string,
    absence_duration: number,
    username: string,
  ): Promise<SearchResult[]> {
    return callApi({
      API_URL: ENDPOINTS.SETMANUELASSIGNMENTABSENCE,
      params: {},
      body: { assignment_gui, student_id, absence_duration, username },
      method: 'POST',
    });
  },
  async GetAssignmentByType(
    query: string,
    searchtype: string,
    timezone: string,
  ): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GETASSIGNMENTBYTYPE,
      params: {},
      body: { parameters: query, searchtype: searchtype, timezone: timezone },
      method: 'POST',
    });
  },
  async GetAssignment(id: string) {
    return callApi({
      API_URL: ENDPOINTS.GETASSIGNMENT,
      params: { id },
    });
  },
  async GetAssignmentInfo(id: string) {
    return callApi({
      API_URL: ENDPOINTS.GETASSIGNMENTINFO,
      params: { id },
    });
  },
  async Regi_GetStudentsByTimeBlockId(
    timeBlockId: string,
    teacher_username: string,
    note_type_id: number,
    fromDate?: Moment,
    toDate?: Moment,
  ): Promise<Student[]> {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate: moment(fromDate).format('YYYY-MM-DD'), toDate: moment(toDate).format('YYYY-MM-DD') };
    }
    return callApi({
      API_URL: ENDPOINTS.REGI_GETSTUDENTSBYTIMEBLOCKID,
      params: { timeBlockId, teacher_username, note_type_id },
      filterDates: filterDates,
    });
  },
  async Regi_MarkStudentStatusByTimeBlockIDAndStudentId(
    timeblockId: string,
    studentId: string,
    Type: number,
    username: string,
    auto: any,
    lateMinutes: number,
    reset: boolean,
    tag_ids?: string,
    note?: string,
    authorized?: boolean
  ): Promise<any> {
    return callApi({
      method: 'POST',
      API_URL: ENDPOINTS.REGI_MARKSTUDENTSTATUSBYTIMEBLOCKIDANDSTUDENTID,
      body: { timeblockId, studentId, Type, username, lateMinutes, reset, tag_ids, note, authorized },
      auto: auto,
    });
  },
  async Regi_MarkStudents4AllDayView(jsonArray: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.REGI_MARKSTUDENTS4ALLDAYVIEW,
      body: jsonArray,
      method: 'POST',
    });
  },
  async Regi_MarkAllStudentsStatusByTimeBlockID(
    timeblockId: string,
    Type: string,
    username: string,
    auto: boolean,
    lateMinutes: number,
  ): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.REGI_MARKALLSTUDENTSSTATUSBYTIMEBLOCKID,
      params: { timeblockId, Type, username, lateMinutes },
      auto: auto,
    });
  },
  async Regi_MarkTimeBlockId(timeblockId: string, userName: string, completed: boolean): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.REGI_MARKTIMEBLOCKID,
      params: { timeblockId, userName, completed },
    });
  },
  async Student_Stat_GetTotalPercentageDay(
    studentId: string,
    fromDate: string,
    toDate: string,
    class_id: string,
    coursedescription: string,
    useMerit: boolean,
    usePause: boolean
  ): Promise<TotalPercentageStat[]> {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate, toDate };
    }

    let newapi = true;

    return callApi({
      API_URL: ENDPOINTS.STUDENT_STAT_GETTOTALPERCENTAGEDAY,
      params: { studentId, newapi, useMerit, usePause },
      body: { class_id, coursedescription },
      filterDates: filterDates,
      method: 'POST',
    });
  },
  async Student_Stat_GetCoins(studentId: string, checkonly: boolean): Promise<any[]> {
    return callApi({
      API_URL: ENDPOINTS.STUDENT_STAT_GETCOINS,
      params: { studentId, checkonly },
    });
  },
  async Student_Stat_GetHeatMapDetails(studentId: string, fromDate: String, toDate: string, useMerit: boolean, usePause: boolean) {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate, toDate };
    }

    return callApi({
      API_URL: ENDPOINTS.STUDENT_STAT_GETHEATMAPDETAILS,
      params: { studentId, useMerit, usePause },
      filterDates: filterDates,
    });
  },
  async GetTransactionLog_Coins(studentId: string, transaction_type: number): Promise<any[]> {
    return callApi({
      API_URL: ENDPOINTS.GETTRANSACTIONLOG_COINS,
      params: { studentId, transaction_type },
    });
  },
  async GetTransactionLog_Stats(): Promise<any[]> {
    return callApi({
      API_URL: ENDPOINTS.GETTRANSACTIONLOG_STATS,
      params: {},
    });
  },
  async GetTransactionLog_CoinsAllStudents(transaction_type: number, maxcount: number): Promise<any[]> {
    return callApi({
      API_URL: ENDPOINTS.GETTRANSACTIONLOG_COINSALLSTUDENTS,
      params: { transaction_type, maxcount },
    });
  },
  async MakeTransaction_Coins(studentId: string, transaction_type: number, amount: number, username: string, text: string): Promise<any[]> {
    return callApi({
      API_URL: ENDPOINTS.MAKETRANSACTION_COINS,
      params: { studentId, transaction_type, amount, username },
      body: text,
      method: 'POST',
    });
  },
  async Student_Stat_GetTotalPercentage(studentId: string, fromDate: Moment, toDate: Moment, useMerit: boolean, usePause: boolean): Promise<TotalPercentageStat[]> {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate: moment(fromDate).format('YYYY-MM-DD'), toDate: moment(toDate).format('YYYY-MM-DD') };
    }

    return callApi({
      API_URL: ENDPOINTS.STUDENT_STAT_GETTOTALPERCENTAGE,
      params: { studentId, useMerit, usePause },
      filterDates: filterDates,
    });
  },
  async Check_CheckInCode(clientId: string, code: string): Promise<any[]> {
    return callApi({
      API_URL: ENDPOINTS.CHECK_CHECKINCODE,
      params: { clientId, code },
    });
  },
  async ADM_CourseInfoClassId(classId: string, fromDate?: Moment, toDate?: Moment): Promise<CourseInfo[]> {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate: moment(fromDate).format('YYYY-MM-DD'), toDate: moment(toDate).format('YYYY-MM-DD') };
    }

    return callApi({
      API_URL: ENDPOINTS.ADM_COURSEINFOCLASSID,
      params: { classId },
      filterDates: filterDates,
    });
  },
  async Student_Stat_GetCourseDetails(
    classId: string,
    studentId: string,
    fromDate?: Moment,
    toDate?: Moment,
  ): Promise<CourseDetailsStat[]> {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate: moment(fromDate).format('YYYY-MM-DD'), toDate: moment(toDate).format('YYYY-MM-DD') };
    }

    return callApi({
      API_URL: ENDPOINTS.STUDENT_STAT_GETCOURSEDETAILS,
      params: { classId, studentId },
      filterDates: filterDates,
    });
  },
  async Student_Stat_GetAllCoursesClassIDs(studentId: string, fromDate?: Moment, toDate?: Moment): Promise<any[]> {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate: moment(fromDate).format('YYYY-MM-DD'), toDate: moment(toDate).format('YYYY-MM-DD') };
    }

    return callApi({
      API_URL: ENDPOINTS.STUDENT_STAT_GETALLCOURSESCLASSIDS,
      params: { studentId },
      filterDates: filterDates,
    });
  },
  async Student_Stat_CourseChartOverTime(
    classId: string,
    studentId: string,
    courseName: string,
    fromDate?: Moment,
    toDate?: Moment,
  ): Promise<any[]> {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate: moment(fromDate).format('YYYY-MM-DD'), toDate: moment(toDate).format('YYYY-MM-DD') };
    }

    return callApi({
      API_URL: ENDPOINTS.STUDENT_STAT_COURSECHARTOVERTIME,
      params: { classId, studentId },
      method: 'POST',
      body: { courseName },
      filterDates: filterDates,
    });
  },
  async Student_Stat_GetCoursePercentage(studentId: string, fromDate: Moment, toDate: Moment, useMerit: boolean, usePause: boolean): Promise<CoursePercentage[]> {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate: moment(fromDate).format('YYYY-MM-DD'), toDate: moment(toDate).format('YYYY-MM-DD') };
    }

    return callApi({
      API_URL: ENDPOINTS.STUDENT_STAT_GETCOURSEPERCENTAGE,
      params: { studentId, useMerit, usePause },
      filterDates: filterDates,
    });
  },
  async Student_Stat_GetAbsenceTypesPerCourse(studentId: string, fromDate?: Moment, toDate?: Moment): Promise<PerCourseAbsenceType[]> {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate: moment(fromDate).format('YYYY-MM-DD'), toDate: moment(toDate).format('YYYY-MM-DD') };
    }
    return callApi({
      API_URL: ENDPOINTS.STUDENT_STAT_GETABSENCETYPESPERCOURSE,
      params: { studentId },
      filterDates: filterDates,
    });
  },
  async GetReportData_LUDUS(studentId: string, fromDate?: Moment, toDate?: Moment): Promise<RegistrationStats[]> {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate: moment(fromDate).format('YYYY-MM-DD'), toDate: moment(toDate).format('YYYY-MM-DD') };
    }

    return callApi({
      API_URL: ENDPOINTS.GETREPOTDATA_LUDUS,
      params: { studentId },
      filterDates: filterDates,
    });
  },
  async Student_Stat_GetRegistrations(studentId: string, fromDate?: Moment, toDate?: Moment): Promise<RegistrationStats[]> {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate: moment(fromDate).format('YYYY-MM-DD'), toDate: moment(toDate).format('YYYY-MM-DD') };
    }

    return callApi({
      API_URL: ENDPOINTS.STUDENT_STAT_GETREGISTRATIONS,
      params: { studentId },
      filterDates: filterDates,
    });
  },
  async GetStreak4Student(studentId: string, fromDate: Moment, toDate: Moment, useMerit: boolean, usePause: boolean, checkonly: boolean): Promise<any> {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate: moment(fromDate).format('YYYY-MM-DD'), toDate: moment(toDate).format('YYYY-MM-DD') };
    }

    return callApi({
      method: 'POST',
      API_URL: ENDPOINTS.GETSTREAK4STUDENT,
      body: { studentId, useMerit, usePause, fromDate, toDate, checkonly }
    });
  },
  async CalculateBadges4Student(studentId: string, useMerit: boolean, usePause: boolean): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.CALCULATEBADGES4STUDENT,
      params: { studentId, useMerit, usePause },
    });
  },
  async GetBadges4Student_Splash(studentId: string, updateView: boolean): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GETBADGES4STUDENT_Splash,
      params: { studentId, updateView },
    });
  },
  async GetAllBadges4Student(studentId: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GETALLBADGES4STUDENT,
      params: { studentId },
    });
  },
  async getUserInfo(username: string): Promise<any> {
    let pending = inFlightUserInfo.get(username);
    if (!pending) {
      pending = callApi({
        API_URL: ENDPOINTS.GETUSERINFO,
        params: { username },
      }).finally(() => inFlightUserInfo.delete(username));
      inFlightUserInfo.set(username, pending);
    }
    const data = await pending;
    // Each caller gets its own copy so one component mutating the result can't affect another.
    return data == null ? data : JSON.parse(JSON.stringify(data));
  },
  async Student_CheckInOut(
    studentId: string,
    clientTimeZone: string,
    checkInMethod: string,
    checkIn: boolean,
    lateMinutes: Number,
    fixedLate: boolean,
    debug: boolean,
    useMerit: boolean,
    usePause: boolean
  ): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.STUDENT_CHECKINOUT,
      params: { studentId, clientTimeZone, checkInMethod, checkIn, lateMinutes, fixedLate, debug, useMerit, usePause },
    });
  },
  async Student_GetCheckInOut(studentId: string, clientTimeZone: string, checkInMethod: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.STUDENT_GETCHECKINOUT,
      params: { studentId, clientTimeZone, checkInMethod },
    });
  },
  async GetNote_Tags(interface_Name: string): Promise<NoteTags> {
    return callApi({
      API_URL: ENDPOINTS.GETNOTE_TAGS,
      params: { interface_Name },
    });
  },
  async getAllNote_Tags() {
    return callApi({
      API_URL: ENDPOINTS.GETALLNOTE_TAGS,
      params: {},
    });
  },
  async GetAllStudentNotes(student_sis_id: string, reader_username: string, note_type: string, clientTimeZone: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GETALLSTUDENTNOTES,
      params: { student_sis_id, note_type, clientTimeZone, reader_username },
    });
  },
  async Teacher_Stat_GetStudentsAbsence(
    teacherName: string,
    note_type_id: Number,
    fromDate: Moment,
    toDate: Moment,
    useMerit: boolean,
    usePause: boolean
  ): Promise<StudentsAbsence[]> {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate: moment(fromDate).format('YYYY-MM-DD'), toDate: moment(toDate).format('YYYY-MM-DD') };
    }

    return callApi({
      API_URL: ENDPOINTS.TEACHER_STAT_GETSTUDENTSABSENCE,
      params: { teacherName, note_type_id, useMerit, usePause },
      filterDates: filterDates,
    });
  },
  async Teacher_Stat_GetUnmarkedLessons(teacherName: string, daysback: number): Promise<UnmarkedScheduleItem[]> {
    return callApi({
      API_URL: ENDPOINTS.TEACHER_STAT_GETUNMARKEDLESSONS,
      params: { teacherName, daysback },
    });
  },
  async Teacher_Stat_GetCoursePercentage(teacherName: string, fromDate: Moment, toDate: Moment, useMerit: boolean, usePause: boolean): Promise<CoursePercentageStats[]> {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate: moment(fromDate).format('YYYY-MM-DD'), toDate: moment(toDate).format('YYYY-MM-DD') };
    }

    return callApi({
      API_URL: ENDPOINTS.TEACHER_STAT_GETCOURSEPERCENTAGE,
      params: { teacherName, useMerit, usePause },
      filterDates: filterDates,
    });
  },
  // defunct, idk if ever coming back
  // async Stats_GetMoversAndShakersByTeacherName(teacherName = 'raimonds') {
  //   return callApi(`/api/Stats_GetMoversAndShakersByTeacherName/${teacherName}`);
  // },
  async ADM_CoursePercentageOverTime(classId: string, fromDate: Moment, toDate: Moment, useMerit: boolean, usePause: boolean): Promise<CoursePercentageOverTime[]> {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate: moment(fromDate).format('YYYY-MM-DD'), toDate: moment(toDate).format('YYYY-MM-DD') };
    }
    return callApi({
      API_URL: ENDPOINTS.ADM_COURSEPERCENTAGEOVERTIME,
      params: { classId, useMerit, usePause },
      filterDates: filterDates,
    });
  },
  async ADM_StudentsPerClass_ID(
    classId: string,
    teacher_username: string,
    note_type: Number,
    fromDate: Moment,
    toDate: Moment,
    useMerit: boolean,
    usePause: boolean
  ): Promise<Student[]> {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate: moment(fromDate).format('YYYY-MM-DD'), toDate: moment(toDate).format('YYYY-MM-DD') };
    }
    return callApi({
      API_URL: ENDPOINTS.ADM_STUDENTSPERCLASS_ID,
      params: { classId, teacher_username, note_type, useMerit, usePause },
      filterDates: filterDates,
    });
  },
  async Regi_GetCourseNameByTimeblockID(timeblockID: string): Promise<{ name: string }> {
    return callApi({
      API_URL: ENDPOINTS.REGI_GETCOURSENAMEBYTIMEBLOCKID,
      params: { timeblockID },
    });
  },
  async GetAllLessonDays4Student(studentId: string, startDate: string, endDate: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GETALLLESSONDAYS4STUDENT,
      params: { studentId, startDate, endDate },
    });
  },
  async ListCoursesForStudent(student_id: string, daysback: Number): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.LISTCOURSESFORSTUDENT,
      params: { student_id, daysback },
    });
  },
  async UpdateStudentEntry(student_id: string, course_id: string, course_description: string, username: string) {
    return callApi({
      API_URL: ENDPOINTS.UPDATESTUDENTENTRY,
      body: { student_id, course_id, course_description, username },
      method: 'POST',
      params: {},
    });
  },
  async GenerateMeritList(username: string, clientTimeZone: string, useDepartment: boolean): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GENERATEMERITLIST,
      params: { username, clientTimeZone, useDepartment },
    });
  },
  async GetMeritStudentByTimeBlocks(json: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GETMERITSTUDENTSBYTIMEBLOCKS,
      params: {},
      method: 'POST',
      body: json
    });
  },
  async GetPauseStudentByTimeBlocks(json: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GETPAUSESTUDENTBYTIMEBLOCKS,
      params: {},
      method: 'POST',
      body: json
    });
  },
  async Check4NotFulldays(): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.CHECK4NOTFULLDAYS,
      params: {}
    });
  },
  async GetPause4Timeblock(reservationId: string, fromDate: string, toDate: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GETPAUSE4TIMEBLOCK,
      params: {},
      method: 'POST',
      body: { reservationId, fromDate, toDate }
    });
  },
  async GetPause4TimeblocksAlldayView(json: string, fromDate: string, toDate: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GETPAUSE4TIMEBLOCKSALLDAYVIEW,
      params: {},
      method: 'POST',
      body: { json, fromDate, toDate }
    });
  },
  async GetPause4Students(json: string, fromDate: string, toDate: string, class_id?: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GETPAUSE4STUDENTS,
      params: {},
      method: 'POST',
      body: { json, fromDate, toDate, class_id }
    });
  },
  async GetPause4StudentPerClass(student_id: string, fromDate: string, toDate: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GETPAUSE4STUDENTPERCLASS,
      params: {},
      method: 'POST',
      body: { student_id, fromDate, toDate }
    });
  },
  async GetPause4Class(json: string, fromDate: string, toDate: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GETPAUSE4CLASS,
      params: {},
      method: 'POST',
      body: { json, fromDate, toDate }
    });
  },
  async GetPause4HeatMap(student_id: string, fromDate: string, toDate: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GETPAUSE4HEATMAP,
      params: {},
      method: 'POST',
      body: { student_id, fromDate, toDate }
    });
  },
  async CalculateMeritStudentOnCourse(json: string, fromDate?: string, toDate?: string): Promise<any> {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate, toDate };
    }

    return callApi({
      API_URL: ENDPOINTS.CALCULATEMERITSTUDENTONCOURSE,
      params: {},
      method: 'POST',
      body: json,
      filterDates: filterDates,
    });
  },
  async GetTimeBlocksByStudentIdAndDate(studentId: string, date: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GETTIMEBLOCKSBYSTUDENTIDANDDATE,
      params: { studentId, date }
    });
  },
  async CountActiveRegistrations(
    student_id: string,
    course_id: string,
    course_description: string
  ): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.COUNTACTIVEREGISTRATIONS,
      params: {},
      body: { student_id, course_id, course_description },
      method: 'POST',
    });
  },
  async DeleteActiveRegistraions(
    student_id: string,
    course_id: string,
    course_description: string
  ): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.DELETEACTIVEREGISTRAIONS,
      params: {},
      body: { student_id, course_id, course_description },
      method: 'POST',
    });
  },
  async GetLeaveInfo(
    student_id: string,
    reservation_id: string,
    absence_type: number
  ): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GETLEAVEINFO,
      params: {},
      body: { student_id, reservation_id, absence_type },
      method: 'POST',
    });
  },
  async GetStudentAbsenceRegistrations(studentId: string, fromDate?: string, toDate?: string): Promise<any[]> {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate, toDate };
    }

    return callApi({
      API_URL: ENDPOINTS.GETSTUDENTABSENCEREGISTRATIONS,
      params: { studentId },
      filterDates: filterDates,
    });
  },
  async GetStudentAbsenceWithPredictions(date: string): Promise<any[]> {
    return callApi({
      API_URL: ENDPOINTS.GETSTUDENTABSENCEWITHPREDICTIONS,
      params: { date }
    });
  },

  async CreateAbsenceRequest(
    student_id: string,
    startdatetime: string,
    enddatetime: string,
    guardian_guid: string,
    request_text: string,
    request_type: number,
    author_username?: string
  ): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.CREATEABSENCEREQUEST,
      params: {},
      body: { student_id, startdatetime, enddatetime, guardian_guid, request_text, request_type, author_username },
      method: 'POST',
    });
  },
  async GetAbsenceRequests4Parent(
    guardian_guid: string,
    clientTimeZone: string
  ): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GETABSENCEREQUESTS4PARENT,
      params: {},
      body: { guardian_guid, clientTimeZone },
      method: 'POST',
    });
  },
  async GetAbsenceRequests4Approval(username: string, fromDate?: string): Promise<any> {
    let params = null
    if (fromDate) params = { username, fromDate }
    else params = { username }

    return callApi({
      API_URL: ENDPOINTS.GETABSENCEREQUESTS4APPROVAL,
      params: params
    });
  },
  async UpdateAbsenceRequest(
    request_id: number,
    guardian_guid: string,
    startdatetime: string,
    enddatetime: string,
    request_text: string,
    request_type: number
  ): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.UPDATEABSENCEREQEUST,
      params: {},
      body: { request_id, guardian_guid, startdatetime, enddatetime, request_text, request_type },
      method: 'POST',
    });
  },
  async ApproveDenyRequest(
    request_id: number,
    username: string,
    approved: boolean,
    text: string
  ): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.APPROVEDENYREQUEST,
      params: {},
      body: { request_id, username, approved, text },
      method: 'POST',
    });
  },
  async DeleteAbsenceRequest(request_id: number, username: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.DELETEABSENCEREQUEST,
      params: {},
      body: { request_id, username },
      method: 'POST',
    });
  },
  async GetAttCounter4User(username: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GETATTCOUNTER4USER,
      params: { username }
    });
  },
  async CheckForStudentUpdate(username: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.CHECKFORSTUDENTUPDATE,
      params: { username }
    });
  },
  async Regi_CheckForChanges(
    reservation_ids: string,
    username: string,
    num_of_Seconds: number
  ): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.REGICHECKFORCHANGES,
      params: {},
      body: { reservation_ids, username, num_of_Seconds },
      method: 'POST',
    });
  },
  async CountAbsenceTags(fromDate: string, toDate: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.COUNTABSENCETAGS,
      params: { fromDate, toDate }
    });
  },
  async CountAbsenceTypes(fromDate: string, toDate: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.COUNTABSENCETYPES,
      params: { fromDate, toDate }
    });
  },
  async GetNotesForDash(fromDate?: string, toDate?: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GETNOTESFORDASH,
      params: { fromDate, toDate }
    });
  },
  async GetCourses(fromDate?: string, toDate?: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GETCOURSES,
      params: { fromDate, toDate }
    });
  },
  async GetTrend(range: number, courseId?: string, studentId?: string, fromDate?: string, toDate?: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GETTREND,
      params: {},
      method: 'POST',
      body: { range, courseId, studentId, fromDate, toDate }
    });
  },
  async GetStudents(fromDate?: string, toDate?: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GETSTUDENTS,
      params: { fromDate, toDate }
    });
  },
  async GetAllRegistrations(fromDate?: string, toDate?: string, timezone?: string, daysback?: number, From_Filter?: string, To_Filter?: string): Promise<any> {
    let params = { fromDate, toDate, timezone, daysback: daysback ? daysback : 30, From_Filter, To_Filter }
    if (From_Filter === undefined || From_Filter === null) delete params.From_Filter;
    if (To_Filter === undefined || To_Filter === null) delete params.To_Filter;
    return callApi({
      API_URL: ENDPOINTS.GETALLREGISTRATIONS,
      params: params
    });
  },
  async GetCourseAbsenceForStudent(fromdate: string, todate: string, studentid: string): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GETCOURSEABSENCEFORSTUDENT,
      params: { fromdate, todate, studentid }
    });
  },
  async GetStudentsInfo(timezone: string) {
    return callApi({
      API_URL: ENDPOINTS.GETSTUDENTSINFO,
      params: { timezone },
    });
  },
  async GetReportData_Kilkenny(json: string, fromDate: string, toDate: string) {
    return callApi({
      API_URL: ENDPOINTS.GETREPORTDATA_KILKENNY,
      params: {},
      body: { json, fromDate, toDate },
      method: 'POST',
    });
  },
  async GetCourseList(fromDate: string, toDate: string) {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate, toDate };
    }
    return callApi({
      API_URL: ENDPOINTS.GETCOURSELIST,
      params: {},
      filterDates: filterDates,
      body: {},
      method: 'GET',
    });
  },
  async GetSubjectList(fromDate: string, toDate: string) {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate, toDate };
    }
    return callApi({
      API_URL: ENDPOINTS.GETSUBJECTLIST,
      params: {},
      filterDates: filterDates,
      body: {},
      method: 'GET',
    });
  },
  async GetStudentAbsenceOnCourseAndSubject(fromDate: string, toDate: string, courseList: any, subjectList: any) {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate, toDate };
    }

    let body = {}
    if (courseList && courseList.length > 0) {
      body['courseList'] = courseList;
    }

    if (subjectList && subjectList.length > 0) {
      body['subjectList'] = subjectList;
    }

    return callApi({
      API_URL: ENDPOINTS.GETSTUDENTABSENCEONCOURSEANDSUBJECT,
      params: {},
      filterDates: filterDates,
      body: body,
      method: 'POST',
    });
  },
  async GetStudentAbsenceOnCourse(fromDate: string, toDate: string, json: string) {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate, toDate };
    }
    return callApi({
      API_URL: ENDPOINTS.GETSTUDENTABSENCEONCOURSE,
      params: {},
      filterDates: filterDates,
      body: json,
      method: 'POST',
    });
  },
  async SetFilter4User(user_id: string, filter_id: number, json: string) {
    return callApi({
      API_URL: ENDPOINTS.SETFILTER4USER,
      params: { user_id, filter_id },
      body: { value: json },
      method: 'POST',
    });
  },
  async GetFilters4user(user_id: string) {
    return callApi({
      API_URL: ENDPOINTS.GETFILTERS4USER,
      params: { user_id },
      method: 'GET',
    });
  },
  async Regi_GetTimeblocksDatesByCourseID(course_id: string, days: number) {
    return callApi({
      API_URL: ENDPOINTS.REGI_GETTIMEBLOCKSDATESBYCOURSEID,
      params: { course_id, days },
      method: 'GET',
    });
  },
  async Regi_GetTimeblocksDatesByTeacherID(teacher_id: string, days: number) {
    return callApi({
      API_URL: ENDPOINTS.REGI_GETTIMEBLOCKSDATESBYTEACHERID,
      params: { teacher_id, days },
      method: 'GET',
    });
  },
  async Regi_GetTimeblocksDatesByStudentID(student_id: string, days: number, merit: boolean, pause: boolean) {
    return callApi({
      API_URL: ENDPOINTS.REGI_GETTIMEBLOCKSDATESBYSTUDENTID,
      params: { student_id, days, merit, pause },
      method: 'GET',
    });
  },
  async Regi_GetRegistrationOverView(timeblock_id: string, json: string) {
    return callApi({
      API_URL: ENDPOINTS.REGI_GETREGISTRATIONOVERVIEW,
      params: { timeblock_id },
      body: JSON.stringify(json),
      method: 'POST',
    });
  },
  async Student_Stat_GetAbsenceLogStudentDate(studentId: string, date: string, timezone: string) {
    return callApi({
      API_URL: ENDPOINTS.STUDENT_STAT_GETABSENCELOGSTUDENTDATE,
      params: { studentId, date, timezone },
    });
  },
  async GetAbsenceRegistrations4Day(student_id: string, date: string, timezone?: string) {
    return callApi({
      API_URL: ENDPOINTS.GETABSENCEREGISTRATIONS4DAY,
      params: { student_id, date, timezone },
    });
  },
  async Student_Stat_GetDayviewRegistrations(student_id: string, fromDate: string, toDate) {
    let filterDates = null;
    if (fromDate && toDate) {
      filterDates = { fromDate, toDate };
    }
    return callApi({
      API_URL: ENDPOINTS.STUDENT_STAT_GETDAYVIEWREGISTRATIONS,
      filterDates: filterDates,
      params: { student_id },
    });
  },
  async GetAbsencePrDate(fromdate: string, todate: string) {
    return callApi({
      API_URL: ENDPOINTS.GETABSENCESPRDATE,
      params: { fromdate, todate },
    });
  },
  async GetCurrentLocalDatetime(timezone: string) {
    return callApi({
      API_URL: ENDPOINTS.GETCURRENTLOCALDATETIME,
      params: { timezone },
    });
  },
  async GetAllBadges(active?: boolean) {
    let params = {}
    if(active) params = { active }
    return callApi({
      API_URL: ENDPOINTS.GETALLBADGES,
      params: params,
    });
  },
  async SetBadgesActive(id: Number) {
    return callApi({
      API_URL: ENDPOINTS.SETBADGESACTIVE,
      params: { id },
    });
  },
  async Get_CheckInCode2(clientId: string, valid_seconds: any, deviceId?: string, length?: number): Promise<any> {   
    return callApi({
      API_URL: ENDPOINTS.GETCHECKINCODEV2,
      params: { clientId, valid_seconds, deviceId, length },
    });    
  },
  async GetClient_APP_Settings(clientId: string): Promise<any> {   
    return callApi({
      API_URL: ENDPOINTS.GETCLIENTAPPSETTINGS,
      params: { clientId },
    });
  },
  async GetMissingRegistrations(days: number, teacherIds: any): Promise<any> {
    return callApi({
      API_URL: ENDPOINTS.GETMISSINGREGISTRATIONS,
      params: {days},
      method: 'POST',
      body: teacherIds,
    });
  },
  async GetMissingRegistrationsByTeacher(days: number): Promise<any> {   
    return callApi({
      API_URL: ENDPOINTS.GETMISSINGREGISTRATIONSBYTEACHER,
      params: { days },
    });
  },
};

const sleepRequest = (milliseconds: number, originalRequest: any) => {
  return new Promise((resolve, reject) => {
    setTimeout(() => resolve(instance(originalRequest)), milliseconds);
  });
};

instance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const { config, response } = error;
    const originalRequest = config;

    // track retry attempts on the request config itself
    originalRequest._retryCount = originalRequest._retryCount || 0;
    const MAX_RETRIES = 3;

    if (originalRequest._retryCount >= MAX_RETRIES) {
      console.log('max retries reached, rejecting');
      return Promise.reject(error);
    }

    if (!response) {
      console.log('noResponse, retrying');
      originalRequest._retryCount++;
      return sleepRequest(500, originalRequest);
    }
    if (response.status === 500 || response.status === 404) {
      console.log('500|404, reject interceptor');
      return Promise.reject(error);
    } else {
      console.log('else SleepRequest');
      originalRequest._retryCount++;
      return sleepRequest(500, originalRequest);
    }
  },
);

interface ApiRequestProps {
  URL: string;
  opts?: AxiosRequestConfig;
  method?: Method;
  body: any;
  apiurl: string;
  filterDates?: any;
  auto?: any;
  direct?: boolean;
}

async function apiRequest({ URL, opts, method, body, apiurl, filterDates, auto, direct }: ApiRequestProps): Promise<any> {
  let headers = { 'Content-Type': 'application/json' };
  if (direct) {
    headers['ApiKey'] = DIRECT_WEBSERVICE_API_KEY;
  } else if (apiurl) {
    headers['apiurl'] = apiurl;
  }
  if (filterDates) {
    headers['fromdate'] = filterDates.fromDate;
    headers['todate'] = filterDates.toDate;
  }
  if (auto !== undefined) headers['auto'] = auto;

  const request = await instance(URL, {
    method: method,
    data: body,
    headers: headers,
    ...opts,
    validateStatus: function (status) {
      return status < 500; // Resolve only if the status code is less than 500
    },
  }).catch(function (error) {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.log(error.response.data);
      console.log(error.response.status);
      console.log(error.response.headers);
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in node.js
      console.log(error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.log('Error', error.message);
    }
    if (error.config) {
      console.log(error.config);
    }
  });
  if (request) {
    // validateStatus above resolves (rather than rejects) anything under 500, so a 401/403 from
    // RoleAuthorizationFilter/AuthenticationAppKeyFilter lands here with a truthy JSON error body
    // instead of throwing - callers checking `if (!success)` would never see the failure and skip
    // any revert-on-failure logic. Same through UnmanagedApi, which passes the webservice's 401/403
    // (and its reason) on as they are.
    if (request.status === 401 || request.status === 403) {
      console.log('Direct webservice call denied', request.status, request.data);
      if (sentExpiredToken(request.config)) {
        // Expected: the token had already expired and couldn't be renewed (typically a sleeping
        // device woken briefly - no network for the renewal). Nothing for the server side to fix,
        // so a breadcrumb rather than a warning. The request is still sent rather than skipped:
        // direct endpoints without a role check (e.g. student check-in) keep working regardless.
        Bugsnag.leaveBreadcrumb('Direct webservice call denied with an expired token', { url: URL, status: request.status });
      } else {
        reportDirectAuthDenied(URL, method, request.status, request.data);
      }
      return null;
    }
    // Same validateStatus: a 404/405 resolves too, and its body is an error page (e.g. a Static
    // Web App's 404 HTML) - never data a caller can use, and handing it back made callers crash on
    // things like `.forEach is not a function` instead of seeing the request had failed.
    if (request.status === 404 || request.status === 405) {
      return null;
    }
    return request.data;
  }
  return null;
}

// Whether the request went out with a bearer token that had already expired.
function sentExpiredToken(config: any): boolean {
  const header = config?.headers?.get?.('Authorization') ?? config?.headers?.Authorization;
  const token = typeof header === 'string' && header.startsWith('Bearer ') ? header.slice('Bearer '.length) : null;
  return !!token && tokenIsExpired(token);
}

// All 401/403s from the direct webservice share one groupingHash so they show up as a single
// Bugsnag error group; the endpoint is kept as context/metadata to tell them apart inside it.
function reportDirectAuthDenied(URL: string, method: Method | undefined, status: number, data: any) {
  let endpoint = URL;
  try {
    // Drop the query string - it carries student/user ids we don't want in Bugsnag.
    endpoint = new window.URL(URL).pathname;
  } catch {}
  const error = new Error(`Direct webservice ${status}: ${endpoint}`);
  error.name = 'DirectWebserviceAuthDenied';
  Bugsnag.notify(error, (event) => {
    event.groupingHash = 'direct-webservice-auth-denied';
    event.severity = 'warning';
    event.context = endpoint;
    event.addMetadata('request', {
      endpoint,
      method: method ?? 'GET',
      status,
      response: data,
    });
  });
}

function reportMissingWebserviceBaseUrl(endpoint: ENDPOINTS) {
  const error = new Error(`No webservice base URL for ${endpoint}`);
  error.name = 'MissingWebserviceBaseUrl';
  Bugsnag.notify(error, (event) => {
    event.groupingHash = 'missing-webservice-base-url';
    event.severity = 'warning';
    event.context = endpoint;
  });
}

interface ApiCallProps {
  API_URL: ENDPOINTS;
  params?: any;
  opts?: AxiosRequestConfig;
  method?: Method;
  body?: any;
  filterDates?: any;
  auto?: any;
}
async function callApi({ API_URL, params, opts = {}, method = 'GET', body, filterDates, auto }: ApiCallProps): Promise<any> {
  const direct =
    USE_DIRECT_WEBSERVICE && DIRECT_WEBSERVICE_ENDPOINTS.includes(API_URL) && !isDemoMode();
  const URL = constructURL(API_URL, params, direct, auto, filterDates);
  if (!URL) {
    // No school webservice URL. Normal right after logout (requests still in flight) - only worth
    // reporting while someone is actually signed in.
    if (useStore.getState().user) reportMissingWebserviceBaseUrl(API_URL);
    return null;
  }
  const source = axios.CancelToken.source();
  opts.cancelToken = source.token;
  //let apiurl = 'https://attenderdevfunctionwebservice.azurewebsites.net/api';
  let apiurl = getWebserviceBaseUrl();
  const data = await apiRequest({
    URL,
    opts,
    method,
    body,
    apiurl,
    filterDates,
    auto,
    direct,
  });
  return data;
}

function constructURL(API_URL: ENDPOINTS, params: any, direct: boolean = false, auto?: any, filterDates?: any) {
  if (direct) {
    return constructDirectWebserviceURL(API_URL, params, auto, filterDates);
  }

  const baseUrl = 'https://unmanagedapi.azurewebsites.net';

  if (!params) return `${baseUrl}/${API_URL}`;

  let paramsString = '';
  for (let key in params) {
    if (params.hasOwnProperty(key)) {
      if (paramsString.length > 0) {
        paramsString += '&' + key + '=' + params[key];
      } else {
        paramsString = '?' + key + '=' + params[key];
      }
    }
  }

  return `${baseUrl}/${API_URL}${paramsString}`;
}

// ClientDataAPI_WS's routes take these as path segments, not query params. UnmanagedApi does
// this same translation server-side today (reads query params, rebuilds a path-segment URL)
// when proxying onward - replicated here per-endpoint since there's no middle layer left to
// do it once this is called directly. See UnmanagedApi's src/functions/<EndpointName>.ts for
// the source of truth on each endpoint's exact downstream path shape.
// The school's webservice base URL (ws_api_url from useAuthBase.getClientData): the store first,
// sessionStorage as a fallback since it survives a reload (before getClientData has run again).
// Null when neither holds a usable URL - e.g. requests still in flight after logout() cleared both,
// which used to go out as "null/<endpoint>" against the site itself (404 page instead of data).
export function getWebserviceBaseUrl(): string | null {
  const isUsable = (value: unknown): value is string => typeof value === 'string' && /^https?:\/\//i.test(value);
  const fromStore = useStore.getState().apiUrl;
  if (isUsable(fromStore)) return fromStore;
  const fromSession = sessionStorage.getItem('api_url');
  return isUsable(fromSession) ? fromSession : null;
}

// Null when there's no usable base URL - callApi() then skips the request instead of sending it.
export function constructDirectWebserviceURL(API_URL: ENDPOINTS, params: any, auto?: any, filterDates?: any): string | null {
  const baseUrl = getWebserviceBaseUrl();
  if (!baseUrl) return null;

  switch (API_URL) {
    case ENDPOINTS.REGI_MARKTIMEBLOCKID:
      return `${baseUrl}/Regi_MarkTimeBlockId/${params.timeblockId}/${params.userName}/${params.completed}`;
    case ENDPOINTS.REGI_MARKALLSTUDENTSSTATUSBYTIMEBLOCKID: {
      const autoStr = String(auto) === 'true' ? 'true' : 'false';
      return `${baseUrl}/Regi_MarkAllStudentsStatusByTimeBlockID/${params.timeblockId}/${params.Type}/${params.username}/${autoStr}/${params.lateMinutes}`;
    }
    case ENDPOINTS.REGI_MARKSTUDENTSTATUSBYTIMEBLOCKIDANDSTUDENTID:
      // JSON-body POST - no path params to translate.
      return `${baseUrl}/Regi_MarkStudentStatusByTimeBlockIDAndStudentId`;
    // These 3 have no UnmanagedApi source to port from (missing from this checkout - see
    // CourseController.cs directly instead), hand-verified against ClientDataAPI_WS's own routes.
    case ENDPOINTS.GETMISSINGREGISTRATIONSBYTEACHER:
      return `${baseUrl}/GetMissingRegistrationsByTeacher/${params.days}`;
    case ENDPOINTS.GETMISSINGREGISTRATIONS:
      // JSON-body POST (teacherIds) - {days} is the only path param.
      return `${baseUrl}/GetMissingRegistrations/${params.days}`;
    case ENDPOINTS.GETSICKEVENT4ALLSTUDENTS:
      return `${baseUrl}/GetSickEvent4AllStudents/${params.daysback}`;
    case ENDPOINTS.ADM_COURSESTAT2EXCEL:
      // New endpoint, never had a middle-layer entry (Export_CourseStatistic built its own Excel
      // file server-side instead of calling this directly) - route shape hand-verified against
      // CourseController.cs.
      return `${baseUrl}/ADM_CourseStat2Excel/${params.course_sis_id}/${params.useMerit}/${params.usePause}/${params.fromDate}/${params.toDate}`;
    default: {
      const builder = DIRECT_ROUTE_BUILDERS[API_URL];
      if (builder) {
        return `${baseUrl}/${builder(params, filterDates)}`;
      }
      // No generated builder (endpoint wasn't auto-portable, or is genuinely param-free) -
      // falls back to the bare endpoint name, same shape as ADM_SubjectOnCourseStat2Excel and
      // UPDATESTUDENTROSTER (body-only, no path/query params - see the DIRECT_WEBSERVICE_ENDPOINTS
      // comment above). The ~3 remaining hand-skipped special cases (search,
      // Export_CourseStatistic [excel4node file], GetStreak4Student) still aren't in that list.
      return `${baseUrl}/${API_URL.replace(/^api\//, '')}`;
    }
  }
}
